import React, { useEffect, useState } from "react";
import { getOrdersByCustomerId } from "../../services/orderService";
import { Link } from "react-router-dom";
import { RightCircleFilled, ReloadOutlined } from "@ant-design/icons";
import {
  Table,
  Badge,
  Button,
  Spin,
  Alert,
  Pagination,
  Card,
  Space,
  Typography,
  Result
} from "antd";
import { useApiCall } from "../../hooks/useApiCall";
import { formatDateTime } from "../../utils/formatters";
import { getStatusInfo } from "../../constants/statusOptions";

const { Title } = Typography;

const OrdersByCustomerPage = () => {
  const [ordersData, setOrdersData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Use the error handling hook
  const { loading, error, execute } = useApiCall();
  
  const pageSize = 10;
  const userId = localStorage.getItem("userId");

  const fetchOrders = async (page) => {
    if (!userId) {
      return;
    }

    try {
      const response = await execute(
        () => getOrdersByCustomerId(userId, page - 1, pageSize)
      );
      
      setOrdersData(response);
    } catch (error) {
      // Error is automatically handled by the useApiCall hook
      console.error("Error fetching orders:", error);
      setOrdersData(null);
    }
  };

  useEffect(() => {
    fetchOrders(currentPage);
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetry = () => {
    fetchOrders(currentPage);
  };

  const columns = [
    {
      title: "Sipariş No",
      dataIndex: "orderReference",
      key: "orderReference",
    },
    {
      title: "Sipariş Tarihi",
      dataIndex: "orderDate",
      key: "orderDate",
      render: (date) => formatDateTime(date),
    },
    {
      title: "Durum",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const { color, text } = getStatusInfo(status, "order");
        return <Badge status={color} text={text} />;
      },
    },    
    {
      title: "Notlar",
      dataIndex: "notes",
      key: "notes",
      render: (text) => text || "-",
    },
    {
      title: "Oluşturan ID",
      dataIndex: "createdByUserId",
      key: "createdByUserId",
    },
    {
      title: "Eylemler",
      key: "actions",
      render: (_, record) => (
        <Link to={`/order-items/${record.id}`}>
          <Button type="primary" size="small" icon={<RightCircleFilled/>}>
            Detaylar
          </Button>
        </Link>
      ),
    },
  ];

  // Handle case where userId is missing
  if (!userId) {
    return (
      <div style={{ padding: "24px" }}>
        <Result
          status="warning"
          title="Şirket Bilgisi Bulunamadı"
          subTitle="Lütfen tekrar giriş yapın."
          extra={
            <Button type="primary" onClick={() => window.location.href = "/login"}>
              Giriş Yap
            </Button>
          }
        />
      </div>
    );
  }

  // Handle major errors when no data is available
  if (error && !loading && !ordersData) {
    return (
      <div style={{ padding: "24px" }}>
        <Result
          status="error"
          title="Siparişler Yüklenemedi"
          subTitle="Siparişleriniz yüklenirken bir hata oluştu. Lütfen tekrar deneyin."
          extra={
            <Button type="primary" icon={<ReloadOutlined />} onClick={handleRetry}>
              Tekrar Dene
            </Button>
          }
        />
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: 50 }}>
        <Spin tip="Siparişler yükleniyor..." size="large" />
      </div>
    );
  }

  const orders = ordersData?.content || [];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Title level={4}>Siparişlerim</Title>
          
          {/* Show warning if there was an error but we have cached data */}
          {error && ordersData && (
            <Alert
              message="Veriler güncel olmayabilir"
              description="Son güncellemede bir hata oluştu, gösterilen veriler eski olabilir."
              type="warning"
              showIcon
              closable
              style={{ marginBottom: 16 }}
              action={
                <Button size="small" onClick={handleRetry}>
                  Yenile
                </Button>
              }
            />
          )}
          
          {orders.length === 0 ? (
            <Alert
              message="Sipariş bulunamadı"
              description="Henüz hiç sipariş oluşturmamışsınız."
              type="info"
              showIcon
            />
          ) : (
            <>
              <Table
                dataSource={orders}
                columns={columns}
                rowKey="id"
                pagination={false}
                scroll={{ x: true }}
              />
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={ordersData?.totalElements || 0}
                onChange={(page) => setCurrentPage(page)}
                style={{ textAlign: "center", marginTop: 24 }}
                showSizeChanger={false}
              />
            </>
          )}
          
          <div style={{ textAlign: "right", marginTop: 16 }}>
            <Typography.Text type="secondary">
              Toplam Sipariş: <strong>{ordersData?.totalElements || 0}</strong>
            </Typography.Text>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default OrdersByCustomerPage;